/**
 * GovernancePanel — Amplifyr Governance Work Item Review
 *
 * Displays pending governance items from Preampr's /api/governance/* endpoints.
 * Tier N3 items are shown first (blocking — require immediate action).
 *
 * Fetches from: VITE_PREAMPR_URL (default: http://localhost:3150)
 *
 * SC Level: SC-05
 */

import { useState, useEffect, useCallback } from 'react';

// ─── Types (inline to avoid circular dep with governr-lib in browser) ─────────

type WorkItemStatus =
  | 'research_in_progress'
  | 'research_complete'
  | 'decision_pending'
  | 'approved'
  | 'rejected'
  | 'deferred'
  | 'executed'
  | 'archived';
type WorkItemPriority = 'P1' | 'P2' | 'P3';
type NotificationTier = 'N0' | 'N1' | 'N2' | 'N3';
type ApprovalScope = 'A' | 'B' | 'C';

interface DriftAnalysis {
  whatChanged: string;
  whyItMatters: string;
  affectedSections?: string[];
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  recommendedAction: string;
  isBlocking: boolean;
  sourcesReviewed: string[];
  analyzedAt: string;
  method: string;
}

interface WorkItemAction {
  id: string;
  label: string;
  description: string;
  resolves: boolean;
  endpoint?: string;
  httpMethod?: string;
}

interface WorkItemEvent {
  timestamp: string;
  actor: string;
  action: string;
  note?: string;
}

interface GovernanceWorkItem {
  id: string;
  type: string;
  title: string;
  summary: string;
  status: WorkItemStatus;
  scope: ApprovalScope;
  tier: NotificationTier;
  priority: WorkItemPriority;
  assignee: string;
  blocking?: string[];
  deadline?: string;
  analysis?: DriftAnalysis;
  actions: WorkItemAction[];
  timeline: WorkItemEvent[];
  metadata: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
  resolvedBy?: string;
  resolutionNote?: string;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const PREAMPR_URL = (import.meta as any).env?.VITE_PREAMPR_URL || 'http://localhost:3150';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function riskColor(level: string): string {
  return level === 'critical'
    ? '#ef4444'
    : level === 'high'
      ? '#f97316'
      : level === 'medium'
        ? '#eab308'
        : '#22c55e';
}

function tierLabel(tier: NotificationTier): string {
  return tier === 'N3'
    ? '🚨 BLOCKING'
    : tier === 'N2'
      ? '⚠️ Action Required'
      : tier === 'N1'
        ? '📋 Review'
        : '📌 FYI';
}

function priorityColor(p: WorkItemPriority): string {
  return p === 'P1' ? '#ef4444' : p === 'P2' ? '#f97316' : '#6b7280';
}

function cardClass(tier: NotificationTier): string {
  if (tier === 'N3') return 'amp-gov-card amp-gov-card--blocking';
  if (tier === 'N2') return 'amp-gov-card amp-gov-card--warning';
  return 'amp-gov-card';
}

function tierLabelClass(tier: NotificationTier): string {
  return `amp-gov-tier-label amp-gov-tier-label--${tier.toLowerCase()}`;
}

function btnClass(variant: string): string {
  return `amp-gov-btn amp-gov-btn--${variant}`;
}

// ─── WorkItem Card ────────────────────────────────────────────────────────────

interface WorkItemCardProps {
  item: GovernanceWorkItem;
  onAction: (itemId: string, actionId: string, note?: string) => Promise<void>;
  loading: boolean;
}

function WorkItemCard({ item, onAction, loading }: WorkItemCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState('');
  const [acting, setActing] = useState<string | null>(null);

  const deadline = item.deadline ? new Date(item.deadline) : null;
  const timeLeft = deadline ? Math.round((deadline.getTime() - Date.now()) / 60000) : null;

  const handleAction = async (action: WorkItemAction) => {
    setActing(action.id);
    await onAction(item.id, action.id, note || undefined);
    setActing(null);
  };

  return (
    <div className={cardClass(item.tier)}>
      {/* Header */}
      <div className="amp-gov-card-header">
        <div style={{ flex: 1 }}>
          <div className="amp-gov-badges">
            <span className={tierLabelClass(item.tier)}>{tierLabel(item.tier)}</span>
            <span
              className="amp-gov-badge"
              style={{ color: priorityColor(item.priority), background: 'rgba(0,0,0,0.3)' }}
            >
              {item.priority}
            </span>
            <span
              className="amp-gov-badge"
              style={{ color: '#a3a3ff', background: 'rgba(99,102,241,0.15)' }}
            >
              Scope {item.scope}
            </span>
            {item.analysis && (
              <span
                className="amp-gov-badge"
                style={{ color: riskColor(item.analysis.riskLevel), background: 'rgba(0,0,0,0.3)' }}
              >
                {item.analysis.riskLevel.toUpperCase()} RISK
              </span>
            )}
          </div>
          <div className="amp-gov-card-title">{item.title}</div>
          <div className="amp-gov-card-summary">{item.summary}</div>
        </div>
      </div>

      {/* Deadline */}
      {timeLeft !== null && (
        <div
          className={`amp-gov-deadline ${timeLeft < 60 ? 'amp-gov-deadline--urgent' : 'amp-gov-deadline--warning'}`}
        >
          ⏱{' '}
          {timeLeft > 0
            ? `${timeLeft < 60 ? timeLeft + 'm' : Math.round(timeLeft / 60) + 'h'} remaining`
            : 'OVERDUE'}
        </div>
      )}

      {/* Blocking */}
      {item.blocking && item.blocking.length > 0 && (
        <div className="amp-gov-blocking-tag">🔴 Blocking: {item.blocking.join(', ')}</div>
      )}

      {/* Analysis (collapsible) */}
      {item.analysis && (
        <div>
          <button className="amp-gov-analysis-toggle" onClick={() => setExpanded(!expanded)}>
            {expanded ? '▼' : '▶'} Analysis Details
          </button>
          {expanded && (
            <div className="amp-gov-analysis-content">
              <div className="amp-gov-analysis-box">
                <div className="amp-section-title">What Changed</div>
                <div className="amp-gov-analysis-text">{item.analysis.whatChanged}</div>
              </div>
              <div className="amp-gov-analysis-box">
                <div className="amp-section-title">Why It Matters</div>
                <div className="amp-gov-analysis-text" style={{ whiteSpace: 'pre-line' }}>
                  {item.analysis.whyItMatters}
                </div>
              </div>
              {item.analysis.affectedSections && (
                <div>
                  <div className="amp-section-title">Risk Signals</div>
                  <div className="amp-gov-risk-signals">
                    {item.analysis.affectedSections.map((s) => (
                      <span
                        key={s}
                        className="amp-gov-badge"
                        style={{ color: '#fca5a5', background: 'rgba(239,68,68,0.15)' }}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="amp-gov-analysis-accent">
                <div className="amp-section-title">Recommended Action</div>
                <div className="amp-gov-analysis-accent-text">
                  {item.analysis.recommendedAction}
                </div>
              </div>
              {item.analysis.sourcesReviewed.map((url) => (
                <a key={url} href={url} target="_blank" rel="noreferrer" className="amp-gov-link">
                  🔗 {url}
                </a>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="amp-gov-card-actions">
        <textarea
          className="amp-gov-textarea"
          placeholder="Optional: add a note with your decision..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div className="amp-gov-btn-row">
          {item.actions.map((action) => (
            <button
              key={action.id}
              className={btnClass(action.id)}
              style={{ opacity: loading || !!acting ? 0.6 : 1 }}
              onClick={() => handleAction(action)}
              disabled={loading || !!acting}
              title={action.description}
            >
              {acting === action.id ? '...' : action.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────────

export function GovernancePanel() {
  const [items, setItems] = useState<GovernanceWorkItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actioning, setActioning] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${PREAMPR_URL}/api/governance/pending`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { items: GovernanceWorkItem[] };
      setItems(data.items ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAction = async (itemId: string, actionId: string, note?: string) => {
    setActioning(true);
    try {
      const endpoint = `${PREAMPR_URL}/api/governance/${itemId}/${actionId}`;
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note, actor: 'dbeer' }),
      });
      const data = await res.json();
      if (res.ok) {
        showToast(data.message || 'Done', true);
        await load(); // Refresh
      } else {
        showToast(data.error || 'Action failed', false);
      }
    } catch (e) {
      showToast((e as Error).message, false);
    } finally {
      setActioning(false);
    }
  };

  const handleSeedEula = async () => {
    setActioning(true);
    try {
      const res = await fetch(`${PREAMPR_URL}/api/governance/seed/eula-drift`, { method: 'POST' });
      const data = await res.json();
      if (data.created?.length > 0) {
        showToast(`Created ${data.created.length} governance item(s)`, true);
        await load();
      } else {
        showToast('No new drift detected (or items already exist)', false);
      }
    } catch (e) {
      showToast((e as Error).message, false);
    } finally {
      setActioning(false);
    }
  };

  // Sort: N3 first, then by priority
  const sorted = [...items].sort((a, b) => {
    const tierOrder = { N3: 0, N2: 1, N1: 2, N0: 3 };
    const priOrder = { P1: 0, P2: 1, P3: 2 };
    const t = tierOrder[a.tier] - tierOrder[b.tier];
    if (t !== 0) return t;
    return priOrder[a.priority] - priOrder[b.priority];
  });

  const blocking = sorted.filter((i) => i.tier === 'N3');
  const other = sorted.filter((i) => i.tier !== 'N3');

  return (
    <div className="amp-gov-panel">
      {/* Header */}
      <div className="amp-gov-header">
        <div>
          <h2 className="amp-gov-title">Governance</h2>
          <div className="amp-gov-subtitle">
            Items requiring review before automated systems can proceed
          </div>
        </div>
        <div className="amp-gov-actions">
          <button className={btnClass('secondary')} onClick={handleSeedEula} disabled={actioning}>
            Detect EULA Drift
          </button>
          <button className={btnClass('secondary')} onClick={load} disabled={loading || actioning}>
            {loading ? '...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`amp-gov-toast ${toast.ok ? 'amp-gov-toast--success' : 'amp-gov-toast--error'}`}
        >
          {toast.ok ? '✅' : '❌'} {toast.msg}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="amp-gov-error">
          ⚠️ Could not load governance items from Preampr ({PREAMPR_URL}): {error}
          <div className="amp-gov-error-hint">
            Make sure Preampr is running: <code>preampr start</code>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && items.length === 0 && (
        <div className="amp-gov-empty">
          <div className="amp-gov-empty-icon">✅</div>
          No pending governance items.
          <div className="amp-gov-empty-hint">
            Click "Detect EULA Drift" to check for policy changes.
          </div>
        </div>
      )}

      {/* Blocking items */}
      {blocking.length > 0 && (
        <div>
          <div className="amp-section-title amp-gov-section-title--blocking">
            🚨 BLOCKING — Automated operations paused ({blocking.length})
          </div>
          <div className="amp-gov-card-list">
            {blocking.map((item) => (
              <WorkItemCard key={item.id} item={item} onAction={handleAction} loading={actioning} />
            ))}
          </div>
        </div>
      )}

      {/* Other items */}
      {other.length > 0 && (
        <div>
          <div className="amp-section-title">Pending Review ({other.length})</div>
          <div className="amp-gov-card-list">
            {other.map((item) => (
              <WorkItemCard key={item.id} item={item} onAction={handleAction} loading={actioning} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
